import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import ConflictResolverModal from "@/components/ConflictResolverModal";
import DiffView from "@/components/DiffView";
import SuggestionProgress from "@/components/SuggestionProgress";
import {
  approveSuggestion,
  getSkill,
  getSuggestion,
  getSuggestionDiff,
  processSuggestion,
  rejectSuggestion,
} from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useSkillPath } from "@/lib/skillPath";
import type { FileDiff, SuggestionDetail } from "@/lib/types";
import { useDelayedFlag } from "@/lib/useDelayedFlag";

// reports are Record<string, unknown> — small helper to read fields for display
function f(obj: Record<string, unknown> | null, key: string): unknown {
  return obj ? obj[key] : undefined;
}

/**
 * A single suggestion's detail + owner actions. Reused by the standalone
 * `/skill/[skillSlug]/suggestions/[suggestionId]` page and inline in the dashboard.
 * Pass `onBack` for an in-place back button (keeps the dashboard sidebar); omit it to use
 * browser back (`router.back()`).
 */
export default function SuggestionView({
  slug,
  id,
  onBack,
}: {
  slug: string;
  id: string;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const skillPath = useSkillPath();
  const { accessToken, loading: authLoading } = useAuth();
  const [sug, setSug] = useState<SuggestionDetail | null>(null);
  const [diff, setDiff] = useState<FileDiff[]>([]);
  const [canReview, setCanReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [skillName, setSkillName] = useState<string>("");
  // Defer the "Loading…" placeholder so a fast load goes straight to content without a flash.
  const showLoading = useDelayedFlag(!sug);

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      // Fetch in parallel — the diff and skill don't depend on the suggestion payload.
      const [s, skill, d] = await Promise.all([
        getSuggestion(id, accessToken),
        getSkill(slug, accessToken).catch(() => null),
        getSuggestionDiff(id, accessToken).catch(() => ({ files: [] })),
      ]);
      setSug(s);
      setSkillName(skill?.name ?? "");
      setCanReview(!!skill?.can_edit);  // owner OR write collaborator (team co-maintainer / grant)
      setDiff(d.files);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Not found");
    }
  }, [id, slug, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!sug || sug.status !== "processing") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [sug, load]);

  // No back button in the suggestion view — return via the dashboard sidebar / browser back.
  const back = null;

  // Only prompt to log in once the session restore has actually finished — otherwise a hard
  // refresh flashes "Log in" before the token arrives.
  if (!authLoading && !accessToken)
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="text-text/70">Log in to view this suggestion.</p>
        <Link to="/login" className="mt-4 inline-block rounded-xl bg-primary px-5 py-2.5 font-medium text-white shadow-lg shadow-primary/20 transition hover:opacity-90">
          Log in
        </Link>
      </div>
    );
  if (error && !sug) return <div className="mx-auto max-w-5xl">{back}<p className="mt-4 text-text/60">{error}</p></div>;
  // Session restoring or the suggestion still fetching — render nothing until the load runs
  // long enough to warrant a placeholder, so fast loads go straight to content with no flash.
  if (!sug) return showLoading ? <div className="mx-auto max-w-5xl">{back}<p className="mt-4 text-text/50">Loading…</p></div> : null;

  async function act(fn: (i: string, t: string) => Promise<unknown>) {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      await fn(id, accessToken);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  // Status pill — a coloured dot + label, styled like the site's mono data chips.
  const status: { label: string; dot: string; text: string; ring: string } =
    sug.status === "merged"
      ? { label: "Approved", dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", ring: "border-emerald-500/30 bg-emerald-500/[0.08]" }
      : sug.status === "rejected"
        ? { label: "Rejected", dot: "bg-red-500", text: "text-red-600 dark:text-red-400", ring: "border-red-500/30 bg-red-500/[0.08]" }
        : sug.status === "conflict"
          ? { label: "Needs resolution", dot: "bg-red-500", text: "text-red-600 dark:text-red-400", ring: "border-red-500/30 bg-red-500/[0.08]" }
          : sug.status === "ready"
            ? { label: "Ready to approve", dot: "bg-primary", text: "text-primary", ring: "border-primary/30 bg-primary/[0.08]" }
            : sug.status === "processing"
              ? { label: "Reviewing", dot: "bg-amber-500 motion-safe:animate-pulse", text: "text-amber-600 dark:text-amber-300", ring: "border-amber-500/30 bg-amber-500/[0.08]" }
              : { label: "Pending", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-300", ring: "border-amber-500/30 bg-amber-500/[0.08]" };

  // Prefer each agent's concise `summary`; fall back to older reports that only have arrays/rationale.
  const criticReasons = (f(sug.critic_report, "reasons") as string[]) || [];
  const criticText = (f(sug.critic_report, "summary") as string) || criticReasons.join(" ");
  const feedbackText =
    (f(sug.feedback_report, "summary") as string) || (f(sug.feedback_report, "rationale") as string) || "";
  const securityText = (f(sug.security_report, "summary") as string) || "";

  const hasResult = sug.critic_report || sug.security_report || sug.feedback_report;
  const hasChangelog = (sug.status === "ready" || sug.status === "merged") && sug.changelog;
  const ownerActions = canReview && sug.status !== "merged" && sug.status !== "rejected";

  // Description always sits directly under the title (above the pipeline), in every status.
  const descriptionSection = (
    <section className="mt-8">
      <Eyebrow>Description</Eyebrow>
      <p className="mt-2.5 whitespace-pre-wrap text-sm leading-relaxed text-text/80">
        {sug.message}
      </p>
    </section>
  );

  return (
    <div className="w-full">
      {back}

      {/* Header — the skill it targets + who raised it come first as context, then the
          suggestion title as the heading. */}
      <div className="mt-5">
        {/* Context row — skill it targets + who raised it, with the status pill at the right end. */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <Link
              to={skillPath(slug)}
              onClick={(e) => {
                // On the standalone detail page (no onBack), act as a real "back to skill" — history
                // back, so the skill's own Back button then returns to the true origin rather than
                // bouncing into this suggestion. When embedded (dashboard), keep the normal link.
                if (!onBack) {
                  e.preventDefault();
                  navigate(-1);
                }
              }}
              className="max-w-full truncate font-display text-2xl font-semibold tracking-tight text-text/90 transition first-letter:uppercase hover:text-accent"
            >
              {skillName || slug}
            </Link>
            <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide ${sug.source === "agent" ? "border-accent/30 bg-accent/10 text-accent" : "border-primary/30 bg-primary/10 text-primary"}`}>
              {sug.source === "agent" ? "agent" : "user"}
            </span>
            {sug.status === "rejected" && sug.reject_stage === "error" && (
              <span className="text-red-500 dark:text-red-400">· processing failed</span>
            )}
            {sug.status === "rejected" && sug.reject_stage && sug.reject_stage !== "error" && sug.reject_stage !== "owner" && (
              <span className="text-red-500/80 dark:text-red-400/70">· did not pass review</span>
            )}
            {sug.status === "rejected" && sug.reject_stage === "owner" && (
              <span className="text-text/40">· owner rejected</span>
            )}
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide ${status.ring} ${status.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        </div>

        {/* Title row — the suggestion title, with the owner's actions pinned to the right end. */}
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <Eyebrow>Title</Eyebrow>
            <h1 className="mt-1.5 font-display text-2xl font-semibold tracking-tight text-text">
              {sug.title || "Suggestion"}
            </h1>
          </div>
          {ownerActions && (
            <div className="flex shrink-0 flex-wrap items-center gap-2.5">
              {sug.status === "conflict" ? (
                <PrimaryAction onClick={() => setResolving(true)} icon={<MergeIcon />}>Resolve conflict</PrimaryAction>
              ) : (
                <>
                  {sug.status === "pending" && (
                    <PrimaryAction onClick={() => act(processSuggestion)} disabled={busy} icon={<PlayIcon />}>
                      {busy ? "Starting…" : "Process"}
                    </PrimaryAction>
                  )}
                  {sug.status === "ready" && (
                    <PrimaryAction onClick={() => act(approveSuggestion)} disabled={busy} icon={<CheckIcon />}>
                      {busy ? "Merging…" : "Approve & merge"}
                    </PrimaryAction>
                  )}
                  <button
                    onClick={() => act(rejectSuggestion)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-black/12 px-4 py-2.5 text-sm font-medium text-text/80 transition hover:border-red-400/60 hover:bg-red-500/[0.06] hover:text-red-600 disabled:opacity-50 dark:border-white/15 dark:hover:text-red-400"
                  >
                    <XIcon /> Reject
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* GitHub-sync helper note (only for that source, while pending). */}
        {ownerActions && sug.status === "pending" && sug.agent_name === "github-sync" && (
          <p className="mt-2.5 text-xs text-text/50">
            This change came from GitHub — click Process to run the security review, then approve to pull it in.
          </p>
        )}
      </div>

      {/* Conflict explainer — GitHub and the platform both changed since they were last in sync. */}
      {sug.status === "conflict" && (
        <div className="mt-4 rounded-2xl border border-red-500/30 bg-red-500/[0.08] px-4 py-3 text-sm text-red-700 dark:text-red-200">
          GitHub and the platform both changed since they were last in sync. Resolve the conflicting
          lines to produce a merged version — it then runs the normal review (quality + security)
          before you approve.
        </div>
      )}

      {sug.status === "conflict" && !canReview && (
        <p className="mt-3 text-sm text-text/50">The skill owner needs to resolve this conflict.</p>
      )}

      {/* Non-owner, in-flight: no actions to offer — surface where it stands in the owner's queue. */}
      {!canReview && sug.status === "pending" && (
        <p className="mt-3 text-sm text-text/50">The skill owner hasn't started the review yet.</p>
      )}
      {!canReview && sug.status === "processing" && (
        <p className="mt-3 text-sm text-text/50">The review is running — the owner will decide once it completes.</p>
      )}
      {!canReview && sug.status === "ready" && (
        <p className="mt-3 text-sm text-text/50">Reviewed — awaiting the owner's approval to merge.</p>
      )}

      {error && (
        <p className="mt-4 rounded-xl border border-red-500/30 bg-red-500/[0.08] px-3.5 py-2.5 text-sm text-red-700 dark:text-red-300">{error}</p>
      )}

      {/* Description — always directly under the title. */}
      {descriptionSection}

      {/* Review pipeline — full-width responsive stepper (horizontal on wide screens,
          vertical on narrow). Not applicable while still a conflict. */}
      {sug.status !== "conflict" && (
        <section className="mt-7">
          <Eyebrow>Review pipeline</Eyebrow>
          <div className="mt-2.5">
            <SuggestionProgress suggestion={sug} />
          </div>
        </section>
      )}

      {/* Files changed — full width */}
      <section className="mt-8">
        <div className="mb-2.5"><Eyebrow>Files changed</Eyebrow></div>
        {diff.length > 0 ? (
          <DiffView files={diff} />
        ) : sug.status === "conflict" ? (
          <p className="text-sm text-text/40">
            The merged diff appears once you resolve the conflict.
          </p>
        ) : (sug.status === "pending" || sug.status === "processing") ? (
          <p className="text-sm text-text/40">
            The diff will appear once the review pipeline completes.
          </p>
        ) : (
          <p className="text-sm text-text/40">No file changes.</p>
        )}
      </section>

      {/* Pipeline error — an agent failed to produce a usable response */}
      {sug.status === "rejected" && sug.reject_stage === "error" && (
        <section className="mt-8">
          <div className="mb-2.5"><Eyebrow>Outcome</Eyebrow></div>
          <div className="rounded-2xl border border-red-500/30 bg-red-500/[0.06] p-4 text-sm">
            <b className="text-red-600 dark:text-red-300">Processing failed</b>
            <p className="mt-1 whitespace-pre-wrap text-text/70">
              {sug.error || "An agent could not complete this step. Try processing again."}
            </p>
          </div>
        </section>
      )}

      {/* Result — full-width stack of cards */}
      {(hasChangelog || hasResult) && (
        <section className="mt-8">
          <div className="mb-2.5"><Eyebrow>Outcome</Eyebrow></div>
          <div className="flex flex-col gap-3">
            {hasChangelog && (
              <div className="rounded-2xl border border-black/8 bg-black/[0.015] p-4 text-sm dark:border-white/10 dark:bg-white/[0.02]">
                <p className="font-display font-medium text-text">
                  {sug.status === "ready" ? "Proposes " : "Merged as "}
                  <span className="text-accent">{sug.version_bump}</span>
                </p>
                <p className="mt-1.5 line-clamp-4 text-text/60">{sug.changelog}</p>
              </div>
            )}
            {sug.feedback_report && (
              <ResultCard title="Changes prepared" body={feedbackText} />
            )}
            {sug.critic_report && (
              <ResultCard title="Quality review" body={criticText} />
            )}
            {sug.security_report && (
              <div className="rounded-2xl border border-black/8 bg-black/[0.015] p-4 text-sm dark:border-white/10 dark:bg-white/[0.02]">
                <p className="flex items-center gap-2 font-display font-medium text-text">
                  Security check
                  <span className="font-mono text-xs font-normal text-text/55">{String(f(sug.security_report, "total"))}/100</span>
                </p>
                {securityText && <p className="mt-1.5 line-clamp-4 text-text/60">{securityText}</p>}
                {(() => {
                  const caps = f(sug.security_report, "capability_disclosure") as string[] | null;
                  return caps && caps.length > 0 ? (
                    <div className="mt-3">
                      <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-text/45">What this skill does</p>
                      <ul className="space-y-0.5">
                        {caps.map((c, i) => (
                          <li key={i} className="text-xs text-text/60">· {c}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const findings = f(sug.security_report, "critical_findings") as { dimension: string; severity: string; description: string }[] | null;
                  return findings && findings.length > 0 ? (
                    <div className="mt-3">
                      <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-red-500/80 dark:text-red-400/70">Critical findings</p>
                      <ul className="space-y-1">
                        {findings.map((fi, i) => (
                          <li key={i} className="text-xs text-red-600 dark:text-red-300/80">· {fi.description}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
              </div>
            </section>
          )}

      {resolving && accessToken && (
        <ConflictResolverModal
          slug={slug}
          accessToken={accessToken}
          onClose={() => setResolving(false)}
          onResolved={(suggestionId) => {
            setResolving(false);
            // The conflict placeholder is reused as the github-merge suggestion, so we're usually
            // already on its page — just reload to show the pipeline; otherwise navigate to it.
            if (suggestionId === id) load();
            else navigate(skillPath(slug, `/suggestions/${suggestionId}`));
          }}
        />
      )}
    </div>
  );
}

// Filled primary CTA — gradient fill + leading icon, matching the site's prominent actions.
function PrimaryAction({
  onClick,
  disabled,
  icon,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-accent px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/25 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
    >
      {icon}
      {children}
    </button>
  );
}

const btnIc = "h-4 w-4 shrink-0";
function PlayIcon() { return (<svg className={btnIc} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>); }
function CheckIcon() { return (<svg className={btnIc} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>); }
function XIcon() { return (<svg className={btnIc} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>); }
function MergeIcon() { return (<svg className={btnIc} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2.5" /><circle cx="6" cy="18" r="2.5" /><circle cx="18" cy="10" r="2.5" /><path d="M6 8.5v7M8.4 9.2A6 6 0 0 0 15.5 10" /></svg>); }

// Design-system "eyebrow": Space Mono, uppercase, small orange dash prefix — opens every section.
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
      <span className="h-px w-5 bg-primary" />
      {children}
    </span>
  );
}

// A titled result card — a font-display heading over a clamped summary line.
function ResultCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-black/8 bg-black/[0.015] p-4 text-sm dark:border-white/10 dark:bg-white/[0.02]">
      <p className="font-display font-medium text-text">{title}</p>
      {body && <p className="mt-1.5 line-clamp-4 text-text/60">{body}</p>}
    </div>
  );
}
